/**
 * Number formatting (PRD §9.5).
 *
 * Everything here pins {@link NUMERIC_LOCALE}; nothing inherits the interface
 * language. See `constants.ts` for why.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.5, §4.3
 */
import { MINUS, NUMERIC_LOCALE, SIGNIFICANT_FIGURES, type Precision } from './constants';

/**
 * Replaces the hyphen-minus a formatter emits with a true minus (U+2212).
 *
 * PRD §9.5 asks for the real character: a hyphen is narrower than the plus it
 * pairs with, so a column of mixed signs looks visually ragged.
 */
export function toTrueMinus(value: string): string {
  return value.replace(/-/g, MINUS);
}

/** True when the input is a finite number this module can format. */
export function isFormattable(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Formats a number with comma thousands and full-stop decimals.
 *
 * `maximumFractionDigits` defaults to the same value as the minimum so a whole
 * number stays whole: `1000` is `1,000`, never `1,000.00`.
 */
export function formatNumber(
  value: number,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  if (!isFormattable(value)) return '';

  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const maximumFractionDigits =
    options.maximumFractionDigits ?? Math.max(minimumFractionDigits, 0);

  return toTrueMinus(
    new Intl.NumberFormat(NUMERIC_LOCALE, {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: true,
    }).format(value),
  );
}

/**
 * Rounds to a significant-figure budget (PRD §9.5: 3 in tables, 2 on headlines).
 *
 * Significant figures, not decimal places: `0.00123` and `123000` both carry
 * three either way, which decimal rounding cannot express with one setting.
 * Trailing zeros are dropped — `12.0` reads as spurious precision when the
 * budget already limited it.
 */
export function formatSignificant(value: number, precision: Precision = 'table'): string {
  if (!isFormattable(value)) return '';
  if (value === 0) return '0';

  const digits = SIGNIFICANT_FIGURES[precision];

  return toTrueMinus(
    new Intl.NumberFormat(NUMERIC_LOCALE, {
      maximumSignificantDigits: digits,
      useGrouping: true,
    }).format(value),
  );
}

/**
 * Formats a percentage. No space before `%` (PRD §9.5).
 *
 * The value is a percentage already (`12.5` → `12.5%`), not a fraction. A
 * function that silently multiplied by 100 would be indistinguishable at the
 * call site from one that did not, and the failure is a factor of 100.
 */
export function formatPercent(value: number, fractionDigits = 0): string {
  if (!isFormattable(value)) return '';

  return `${formatNumber(value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`;
}

/**
 * Formats a signed change, always carrying its sign.
 *
 * A delta without an explicit `+` is ambiguous next to a plain figure, so the
 * plus is added rather than left to the reader.
 */
export function formatDelta(value: number, fractionDigits = 0): string {
  if (!isFormattable(value)) return '';

  const formatted = formatNumber(Math.abs(value), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  if (value > 0) return `+${formatted}`;
  if (value < 0) return `${MINUS}${formatted}`;
  return formatted;
}
